/**
 * The account menu and the progress-as-a-code dialog
 * (components/home/Account.tsx, Backup.tsx, lib/backup.ts, state/useAccount.ts).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * Numbers are formatted through lib/format with an explicit locale rather
 * than the ambient one, because a catalogue is a value: `stringsFor('ar')`
 * has to build Arabic sentences whether or not the page it is being read on
 * is currently Arabic.
 *
 * ## Two sentences here used to be built by their callers
 *
 * `backup.failed.*` were twelve lowercase fragments in lib/backup.ts, each
 * written to complete "That code could not be read: …" — a prefix the dialog
 * pasted on. And `backup.restored` was a function in the dialog that joined
 * "7 levels" and "2,300 XP" with `' and '`, chose between `is` and `are`, and
 * formatted the number with a hardcoded `en-GB`.
 *
 * Neither survives translation. Arabic puts that clause elsewhere in the
 * sentence, so a fragment written to be appended has nowhere to be appended
 * to; and Arabic counts in four forms where English counts in two, so a
 * sentence assembled from pieces arrives in the wrong order *and* the wrong
 * number. Both are therefore whole sentences here, one per language, built
 * from facts rather than from words.
 */

import { ltr, num } from '../format';

/** What a restore actually put into this browser, for the one sentence that reports it. */
export interface Restored {
  /**
   * Whether the code matched its signature. False is not a failure — the
   * record is imported and still counts — but the sentence must not read
   * like a success, because nothing vouches for what came back.
   */
  signed: boolean;
  /** Levels, stars and XP that were not here before the merge. Any of them may be zero. */
  levels: number;
  stars: number;
  xp: number;
}

/** `a`, `a and b`, `a, b and c`. */
function list(parts: string[]): string {
  if (parts.length <= 2) return parts.join(' and ');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export const en = {
  /* The corner of the level list that knows who you are. */
  account: {
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signInWhy: 'Save your progress to an account and use it from any browser',
    /* The panel behind the picture, for a screen reader. */
    menu: 'Your account',
    signedInAs: (name: string) => `Signed in as ${name}`,
    what: 'Your levels, stars and XP are saved to this account and follow you to any browser.',
    signOut: 'Sign out',
    /* The two toasts. Raised from state/useAccount, outside the render tree. */
    toastSignedIn: (name: string) => `Signed in as ${name}.`,
    toastSignedOut: 'Signed out. Your progress stays on this browser.',
  },

  backup: {
    label: 'Progress backup',
    title: 'A copy you own',
    lede:
      'This code restores your levels, stars and XP in any browser, and while you are signed out it is the ' +
      'only copy that survives clearing this site’s data.',

    /* The half the code comes out of. */
    codeLabel: 'Your code',
    codeLoading: 'Reading this browser’s record…',
    copy: 'Copy',
    download: 'Download',
    copied: 'Copied.',
    copiedByHand: 'Selected it instead — this browser would not let the page reach the clipboard. Press Ctrl+C, or Cmd+C.',

    /* The half a code goes into. */
    restoreLabel: 'Restore from a code',
    pastePlaceholder: 'Paste a code here',
    restore: 'Restore',
    restoring: 'Restoring…',
    onlyAdds: 'Restoring only ever adds. Nothing already cleared here is taken away.',

    /* What the last Restore did. One sentence, built from the figures. */
    restored: ({ signed, levels, stars, xp }: Restored) => {
      const parts: string[] = [];
      /* Parallel to `parts`: whether each one is a single thing, for the verb. */
      const lone: boolean[] = [];
      if (levels > 0) {
        parts.push(`${num(levels, 'en')} ${levels === 1 ? 'level' : 'levels'}`);
        lone.push(levels === 1);
      }
      if (stars > 0) {
        parts.push(`${num(stars, 'en')} ${stars === 1 ? 'star' : 'stars'}`);
        lone.push(stars === 1);
      }
      if (xp > 0) {
        parts.push(`${num(xp, 'en')} XP`);
        lone.push(xp === 1);
      }
      const amount = list(parts);
      const verb = parts.length === 1 && lone[0] ? 'is' : 'are';

      if (signed) {
        return amount ? `Restored. ${amount} ${verb} back.` : 'Restored. This browser now holds everything that code was carrying.';
      }
      // Deliberately not a congratulation. The record still counts and still
      // unlocks what it unlocks; what it cannot do is claim to be vouched for.
      const head = 'That code did not match its signature, so something in it changed after it was written.';
      return amount
        ? `${head} ${amount} came back, and every clear among them shows as unverified until you replay the level.`
        : `${head} What came back shows as unverified until you replay it.`;
    },

    nothingNew: 'Nothing new in that code — this browser already has everything in it.',
    notAPassword: 'A code is not a password. It carries no account and opens none.',
    close: 'Close',
    closeWhy: 'Close (Esc)',

    /**
     * The twelve ways a pasted code is not a code.
     *
     * Whole sentences, not fragments: lib/backup.ts picks one and returns it,
     * and the dialog prints it as it is. Every one of them is distinct on
     * purpose — a learner who pasted half a code, one who pasted a code from
     * an older build and one who pasted a recipe should each be told which of
     * those happened, because each has a different next move.
     */
    failed: {
      empty: 'That code was empty.',
      tooLong: 'That is far longer than any progress code, so none of it was read.',
      /* The prefixes end in a dot of their own, so nothing here may put a
         full stop straight after one: `EOS0..` reads as a typo. */
      wrongPrefix: (gzip: string, plain: string) =>
        `A progress code begins with ${gzip} or ${plain} — and that one does not, so it is not a progress code.`,
      notBase64: 'That code is not valid base64, so something was lost copying it.',
      notCompressed: 'That code could not be decompressed, so something was lost copying it.',
      notText: 'That code does not decode to readable text.',
      notARecord: 'What that code contains is not a progress record.',
      wrongVersion: 'That code was written by a different version of the game.',
      badLevels: 'The levels in that code are not readable.',
      noSignature: 'That code is missing its signature.',
      badLevelName: 'That code names a level in a way the game does not recognise.',
      badLevelRecord: (id: string) => `The record for ${id} in that code is not readable.`,
    },
  },
};

/**
 * A counted noun in Arabic, in the four forms the language actually uses.
 *
 * One takes the noun and the word for one, with no digit — `مستوى واحد`,
 * because `1 مستوى` is a form nobody writes. Two takes the dual, which is a
 * word and not a suffix bolted on: `مستويان`, `نجمتان`, `نقطتا خبرة`. Three
 * to ten take the plural after the digit (`7 مستويات`), and eleven and up
 * take the singular after it (`18 مستوى`), which is the rule that surprises
 * people and the reason this is a function rather than an `s`.
 *
 * XP is the one that reaches the large numbers, and it is the one where
 * getting this wrong would show on every restore.
 */
interface Counted {
  one: string;
  two: string;
  /** 3-10. */
  few: string;
  /** 11 and up. */
  many: string;
}

function counted(n: number, forms: Counted): string {
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  return `${num(n, 'ar')} ${n >= 3 && n <= 10 ? forms.few : forms.many}`;
}

const LEVELS: Counted = { one: 'مستوى واحد', two: 'مستويان', few: 'مستويات', many: 'مستوى' };
const STARS: Counted = { one: 'نجمة واحدة', two: 'نجمتان', few: 'نجوم', many: 'نجمة' };
const XP: Counted = { one: 'نقطة خبرة واحدة', two: 'نقطتا خبرة', few: 'نقاط خبرة', many: 'نقطة خبرة' };

export const ar: typeof en = {
  account: {
    signIn: 'سجّل الدخول',
    signingIn: 'جارٍ تسجيل الدخول…',
    signInWhy: 'احفظ تقدمك في حساب واستخدمه من أي متصفح',
    menu: 'حسابك',
    signedInAs: (name: string) => `مسجَّل الدخول باسم ${name}`,
    what: 'تُحفظ مستوياتك ونجومك ونقاط خبرتك في هذا الحساب، لتجد تقدمك في أي متصفح.',
    signOut: 'سجّل الخروج',
    toastSignedIn: (name: string) => `سجّلت الدخول باسم ${name}.`,
    toastSignedOut: 'سجّلت الخروج. سيبقى تقدمك محفوظًا في هذا المتصفح.',
  },

  backup: {
    label: 'نسخة احتياطية للتقدم',
    title: 'احتفظ بنسخة من تقدمك',
    lede:
      'يتيح لك هذا الرمز استعادة مستوياتك ونجومك ونقاط خبرتك في أي متصفح. وإذا لم تكن مسجلًا الدخول، فهو النسخة الوحيدة ' +
      'التي ستبقى لديك بعد مسح بيانات الموقع.',

    codeLabel: 'رمزك',
    codeLoading: 'جارٍ إعداد سجل التقدم…',
    copy: 'انسخ',
    download: 'نزّل',
    copied: 'نُسخ الرمز.',
    /* The two key combinations are isolated: a `+` between two Latin words
       inside a right-to-left line lands on the wrong side of them. */
    copiedByHand: `تعذّر النسخ تلقائيًا لأن المتصفح لا يسمح للصفحة بالوصول إلى الحافظة. حددنا الرمز لك؛ اضغط ${ltr('Ctrl+C')} أو ${ltr('Cmd+C')}.`,

    restoreLabel: 'استعد من رمز',
    pastePlaceholder: 'الصق رمزًا هنا',
    restore: 'استعد',
    restoring: 'جارٍ الاستعادة…',
    onlyAdds: 'تضيف الاستعادة التقدم الناقص فقط، ولن تحذف أي إنجاز موجود في هذا المتصفح.',

    /* A nominal sentence — "X is now in this browser" — rather than a verb
       before the amount, because an Arabic verb in that position agrees with
       whichever noun happens to come first, and which noun comes first here
       depends on what the code was carrying. */
    restored: ({ signed, levels, stars, xp }: Restored) => {
      const parts: string[] = [];
      if (levels > 0) parts.push(counted(levels, LEVELS));
      if (stars > 0) parts.push(counted(stars, STARS));
      if (xp > 0) parts.push(counted(xp, XP));
      /* Arabic lists repeat the و rather than using commas, and it attaches
         to the word after it with no space. */
      const amount = parts.join(' و');

      if (signed) {
        return amount ? `اكتملت الاستعادة. أصبح لديك في هذا المتصفح ${amount}.` : 'اكتملت الاستعادة. أصبح هذا المتصفح يحتوي على كل التقدم المحفوظ في الرمز.';
      }
      const head = 'لا يطابق الرمز توقيعه، ما يعني أن محتواه عُدّل بعد إنشائه.';
      return amount
        ? `${head} استعدنا ${amount} في هذا المتصفح، لكن ستظهر النتائج غير مؤكدة حتى تعيد مستوياتها.`
        : `${head} ستظهر النتائج المستعادة غير مؤكدة حتى تعيد مستوياتها.`;
    },

    nothingNew: 'لا يحتوي الرمز على تقدم جديد؛ كل ما فيه موجود بالفعل في هذا المتصفح.',
    notAPassword: 'هذا ليس رمز دخول أو كلمة مرور، ولا يمنح الوصول إلى أي حساب.',
    close: 'أغلق',
    closeWhy: 'أغلق (Esc)',

    failed: {
      empty: 'الرمز فارغ.',
      tooLong: 'النص أطول بكثير من رمز تقدم صالح، لذلك لم نحاول قراءته.',
      /* The two prefixes are machine text and end in a dot, which is exactly
         what bidi moves to the other end of the run if it is left loose. */
      wrongPrefix: (gzip: string, plain: string) =>
        `يبدأ رمز التقدم بـ ${ltr(gzip)} أو ${ltr(plain)}، لكن النص المدخل لا يبدأ بأي منهما.`,
      notBase64: 'ترميز base64 غير صالح. ربما فُقد جزء من الرمز أثناء نسخه.',
      notCompressed: 'تعذّر فك ضغط الرمز. ربما فُقد جزء منه أثناء النسخ.',
      notText: 'لا يحتوي الرمز على نص يمكن قراءته.',
      notARecord: 'لا يحتوي الرمز على سجل تقدم صالح.',
      wrongVersion: 'أُنشئ هذا الرمز في إصدار غير متوافق من EngineerOS.',
      badLevels: 'تعذّرت قراءة بيانات المستويات داخل الرمز.',
      noSignature: 'الرمز بلا توقيع.',
      badLevelName: 'يحتوي الرمز على اسم مستوى غير معروف.',
      badLevelRecord: (id: string) => `تعذّرت قراءة سجل المستوى ${ltr(id)} داخل الرمز.`,
    },
  },
};

/** "a", "a und b", "a, b und c" — German puts no comma before the last `und`. */
function listDe(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

export const de: typeof en = {
  account: {
    signIn: 'Anmelden',
    signingIn: 'Anmeldung läuft …',
    signInWhy: 'Speichere deinen Fortschritt in einem Konto und nutze ihn in jedem Browser',
    menu: 'Dein Konto',
    signedInAs: (name) => `Angemeldet als ${name}`,
    what: 'Deine Level, Sterne und XP werden in diesem Konto gespeichert und folgen dir in jeden Browser.',
    signOut: 'Abmelden',
    toastSignedIn: (name) => `Angemeldet als ${name}.`,
    toastSignedOut: 'Abgemeldet. Dein Fortschritt bleibt in diesem Browser.',
  },
  backup: {
    label: 'Fortschrittssicherung',
    title: 'Eine Kopie, die dir gehört',
    lede: 'Dieser Code stellt deine Level, Sterne und XP in jedem Browser wieder her, und solange du abgemeldet bist, ist er die einzige Kopie, die das Löschen der Daten dieser Website übersteht.',
    codeLabel: 'Dein Code',
    codeLoading: 'Der Stand dieses Browsers wird gelesen …',
    copy: 'Kopieren',
    download: 'Herunterladen',
    copied: 'Kopiert.',
    copiedByHand: 'Stattdessen markiert – dieser Browser lässt die Seite nicht an die Zwischenablage. Drück Strg+C oder Cmd+C.',
    restoreLabel: 'Aus einem Code wiederherstellen',
    pastePlaceholder: 'Code hier einfügen',
    restore: 'Wiederherstellen',
    restoring: 'Wird wiederhergestellt …',
    onlyAdds: 'Wiederherstellen fügt immer nur hinzu. Nichts, was hier schon geschafft ist, wird weggenommen.',
    restored: ({ signed, levels, stars, xp }) => {
      const parts: string[] = [];
      if (levels > 0) parts.push(`${num(levels, 'de')} Level`);
      if (stars > 0) parts.push(`${num(stars, 'de')} ${stars === 1 ? 'Stern' : 'Sterne'}`);
      if (xp > 0) parts.push(`${num(xp, 'de')} XP`);
      const amount = listDe(parts);
      if (signed) {
        return amount
          ? `Wiederhergestellt. ${amount} sind wieder da.`
          : 'Wiederhergestellt. Dieser Browser hat jetzt alles, was dieser Code enthielt.';
      }
      const head = 'Dieser Code passte nicht zu seiner Signatur, also wurde nach dem Erstellen etwas darin verändert.';
      return amount
        ? `${head} ${amount} sind zurückgekommen, und jeder Abschluss darunter gilt als unbestätigt, bis du das Level noch einmal spielst.`
        : `${head} Was zurückgekommen ist, gilt als unbestätigt, bis du es noch einmal spielst.`;
    },
    nothingNew: 'Nichts Neues in diesem Code – dieser Browser hat schon alles, was darin steht.',
    notAPassword: 'Ein Code ist kein Passwort. Er enthält kein Konto und öffnet keins.',
    close: 'Schließen',
    closeWhy: 'Schließen (Esc)',
    failed: {
      empty: 'Dieser Code war leer.',
      tooLong: 'Das ist viel länger als jeder Fortschrittscode, deshalb wurde nichts davon gelesen.',
      wrongPrefix: (gzip, plain) =>
        `Ein Fortschrittscode beginnt mit ${gzip} oder ${plain} – dieser nicht, also ist er kein Fortschrittscode.`,
      notBase64: 'Dieser Code ist kein gültiges Base64, beim Kopieren ist also etwas verloren gegangen.',
      notCompressed: 'Dieser Code ließ sich nicht entpacken, beim Kopieren ist also etwas verloren gegangen.',
      notText: 'Dieser Code ergibt entschlüsselt keinen lesbaren Text.',
      notARecord: 'Was dieser Code enthält, ist kein Fortschrittsstand.',
      wrongVersion: 'Dieser Code wurde von einer anderen Version des Spiels geschrieben.',
      badLevels: 'Die Level in diesem Code sind nicht lesbar.',
      noSignature: 'Diesem Code fehlt seine Signatur.',
      badLevelName: 'Dieser Code nennt ein Level auf eine Weise, die das Spiel nicht kennt.',
      badLevelRecord: (id) => `Der Eintrag für ${id} in diesem Code ist nicht lesbar.`,
    },
  },
};

export const ja: typeof en = {
  account: {
    signIn: 'ログイン',
    signingIn: 'ログイン中…',
    signInWhy: '進捗をアカウントに保存して、どのブラウザーからでも続けられます',
    menu: 'アカウント',
    signedInAs: (name: string) => `${name}としてログイン中`,
    what: 'レベル、星、XPはこのアカウントに保存され、どのブラウザーでも引き継がれます。',
    signOut: 'ログアウト',
    toastSignedIn: (name: string) => `${name}としてログインしました。`,
    toastSignedOut: 'ログアウトしました。進捗はこのブラウザーに残ります。',
  },
  backup: {
    label: '進捗のバックアップ',
    title: '自分で持てるコピー',
    lede: 'このコードを使うと、どのブラウザーでもレベル、星、XPを復元できます。ログアウト中は、このサイトのデータを消去しても残る唯一のコピーです。',
    codeLabel: 'あなたのコード',
    codeLoading: 'このブラウザーの記録を読み込んでいます…',
    copy: 'コピー',
    download: 'ダウンロード',
    copied: 'コピーしました。',
    copiedByHand: '代わりに選択状態にしました。このブラウザーではページからクリップボードにアクセスできません。Ctrl+C(MacではCmd+C)を押してください。',
    restoreLabel: 'コードから復元',
    pastePlaceholder: 'ここにコードを貼り付け',
    restore: '復元',
    restoring: '復元中…',
    onlyAdds: '復元は追加するだけです。すでにここでクリアした分が消えることはありません。',
    restored: ({ signed, levels, stars, xp }) => {
      const parts: string[] = [];
      if (levels > 0) parts.push(`${num(levels, 'ja')}レベル`);
      if (stars > 0) parts.push(`星${num(stars, 'ja')}個`);
      if (xp > 0) parts.push(`${num(xp, 'ja')} XP`);
      const amount = parts.join('、');
      if (signed) {
        return amount
          ? `復元しました。${amount}が戻りました。`
          : '復元しました。このブラウザーに、そのコードが持っていたすべてが入りました。';
      }
      const head = 'このコードは署名と一致しませんでした。書き出したあとに、中身が変更されています。';
      return amount
        ? `${head}${amount}が戻りましたが、その中のクリアはすべて、レベルをもう一度プレイするまで未検証と表示されます。`
        : `${head}戻ったものは、もう一度プレイするまで未検証と表示されます。`;
    },
    nothingNew: 'このコードに新しいものはありません。中身はすべて、すでにこのブラウザーにあります。',
    notAPassword: 'コードはパスワードではありません。アカウントは含まれておらず、アカウントを開くこともできません。',
    close: '閉じる',
    closeWhy: '閉じる(Esc)',
    failed: {
      empty: 'コードが空でした。',
      tooLong: '進捗コードとしては長すぎるため、まったく読み込まれませんでした。',
      wrongPrefix: (gzip: string, plain: string) =>
        `進捗コードは ${gzip} または ${plain} で始まります。このコードはどちらでもないため、進捗コードではありません。`,
      notBase64: 'このコードは有効なBase64ではありません。コピーの途中で何かが欠けたようです。',
      notCompressed: 'このコードは展開できませんでした。コピーの途中で何かが欠けたようです。',
      notText: 'このコードをデコードしても、読める文字列になりません。',
      notARecord: 'このコードの中身は、進捗の記録ではありません。',
      wrongVersion: 'このコードは、別のバージョンのゲームで書き出されたものです。',
      badLevels: 'このコード内のレベルを読み取れません。',
      noSignature: 'このコードには署名がありません。',
      badLevelName: 'このコードには、ゲームが認識できない形式のレベル名が含まれています。',
      badLevelRecord: (id: string) => `このコード内の ${id} の記録を読み取れません。`,
    },
  },
};
